import {
    ApplyFilter,
    FilterInputData,
    FilterListType,
    PLPManager,
    PLPState
} from '../FilterTypes'
import type { Maybe, Scalars } from '@pylot-data/pylotschema'

/**
 * Rebuild a new filter state and trigger product collection reload
 *
 * @param plpManager
 */
export const getApplyFilterHandler = (plpManager: PLPManager): ApplyFilter => {
    const { plpState, setFilter } = plpManager
    return (filterInputData, isApplied = true, isSingleSelect = false) => {
        // Clear all filters
        if (!filterInputData) {
            setFilter({ filters: null, appliedFilterData: [] })
            return
        }

        setFilter({
            filters: buildFilterInput(filterInputData, isApplied, plpState),
            appliedFilterData: getAppliedFilterData(
                filterInputData,
                isApplied,
                isSingleSelect,
                plpState
            )
        })
    }
}

/**
 * Manage the applied filters
 * return {}
 */
export const buildFilterInput = (
    filterInputData: FilterInputData,
    isApplied: boolean,
    state: PLPState
): FilterListType => {
    const { requestVar, value_string } = filterInputData

    if (!isApplied) {
        return deleteFilter(state, value_string, requestVar)
    }

    // 'eq' ensures that we filter only by one sub-category at a time
    if (requestVar === 'category_uid') {
        return {
            ...state.filters,
            ...{ [requestVar]: { ['eq']: value_string } }
        }
    }

    if (requestVar === 'price') {
        return buildPriceInput(state, value_string, requestVar)
    }

    if (requestVar in state.filters) {
        return buildAttributeInput(state, value_string, requestVar)
    }

    return {
        ...state.filters,
        [requestVar]: {
            in: [value_string]
        }
    }
}

const buildPriceInput = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    const priceRange = value_string.split('_')
    const priceStart = priceRange[0]
    const priceEnd = priceRange[1]
    if (priceStart == '*') {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: '0', to: priceEnd } }
        }
    } else if (priceEnd == '*') {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: priceStart } }
        }
    } else if (!isNaN(parseFloat(priceStart)) && !isNaN(parseFloat(priceEnd))) {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: priceStart, to: priceEnd } }
        }
    }
    return state.filters
}

const buildAttributeInput = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    return {
        ...state.filters,
        ...{
            [requestVar]: {
                in: [...(state.filters[requestVar]['in'] ?? []), value_string]
            }
        }
    }
}

// Either delete one option from 'in' condition (if multiple options are selected),
// or delete the whole condition
const deleteFilter = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    let filteredCondition: Maybe<Array<Maybe<Scalars['String']>>> = []
    if (state.filters[requestVar]) {
        const inCondition = state.filters[requestVar]['in'] ?? []
        filteredCondition = inCondition.filter((value: any) => {
            return value !== value_string
        })
    }
    if (filteredCondition && filteredCondition.length > 0) {
        state.filters[requestVar]['in'] = filteredCondition
    } else {
        delete state.filters[requestVar]
    }
    return state.filters
}

// Remove input data of a filter option, then push it again (if the filter is applied)
const getAppliedFilterData = (
    filterInputData: FilterInputData,
    isApplied: boolean,
    isSingleSelect: boolean,
    state: PLPState
): Array<FilterInputData> => {
    const data = state.appliedFilterData.filter(
        (item) =>
            item.requestVar !== filterInputData.requestVar ||
            (!isSingleSelect &&
                item.value_string !== filterInputData.value_string)
    )
    if (isApplied) {
        data.push(filterInputData)
    }
    return data
}
